import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import LayoutWithMap from 'containers/Base/LayoutWithMap';
import useBusinessClients from 'containers/Pages/People/BusinessClients/Layouts/useBusinessClient.js';
import useFilters from 'containers/Base/useFilters.js';
import {
  eventsGridActions,
  eventSelectors,
  eventsFilterActions,
  eventsPageActions,
} from 'containers/Pages/Events';
import { EVENT_INCIDENT_STATUS } from 'utils/constants/eventStatus';
import { getSecurityCompanyServices } from 'reducers/securityCompany';
import { PAGE_URLS } from 'utils/constants';
import { detailOpenState } from 'reducers/ui';

const Events = ({
  defaultFilterValues,
  columns,
  filters,
  filtersRequired,
  DetailComponent,
  FormComponent,
  editable,
  toggleModal,
  onEdit,
  downloadable,
  loading,
}) => {
  const [eventGridApi, setEventGridApi] = useState();
  //--------------------Selectors-----------------------
  const events = useSelector(state =>
    eventSelectors.getEventListTransformed(state),
  );
  const onMapSelectedSiteId = useSelector(state =>
    eventSelectors.getSelectedRowID(state),
  );
  const filterValues = useSelector(state =>
    eventSelectors.getEventFilters(state),
  );
  const totalEntries = useSelector(state =>
    eventSelectors.getTotalRowsCount(state),
  );
  const forceRefreshEvents = useSelector(state =>
    eventSelectors.getForceRefresh(state),
  );
  const services = useSelector(state => getSecurityCompanyServices(state));
  const detailOpen = useSelector(state => detailOpenState(state));
  //-------------------------------------------

  //---------------Hooks-----------------------
  const bizClients = useBusinessClients();
  const { localFilterValues, updateFilter } = useFilters({
    filterValues: {
      ...filterValues,
      incidentStatus: defaultFilterValues.incidentStatus,
    },
  });
  //-----------------------------------------
  const dispatch = useDispatch();
  //--------------Actions------------------------
  const dispatchEventListAction = useCallback(
    ({ filters, from }) => {
      dispatch(eventsGridActions.fetchGridList({ filters, from }));
    },
    [dispatch],
  );
  const dispatchSetFilterAction = useCallback(
    filters => {
      dispatch(eventsFilterActions.setFilters(filters));
    },
    [dispatch],
  );
  const dispatchSetFilterKeyAction = useCallback(
    key => {
      dispatch(eventsFilterActions.setFilterKey(key));
    },
    [dispatch],
  );
  const dispatchPrintEventList = useCallback(
    filters => {
      dispatch(eventsPageActions.printEventList(filters));
    },
    [dispatch],
  );
  const onUpdate = filters => {
    dispatchEventListAction({
      filters,
    });
  };

  const gridAPIHandler = gridAPI => {
    setEventGridApi(gridAPI);
  };

  // from is the pagination parameter as we scroll it changes to a higher number
  const fetchEvents = from => {
    dispatchEventListAction({
      filters: localFilterValues,
      from,
    });
    // keep filter values in sync with redux
    dispatchSetFilterAction(localFilterValues);
  };
  const onDownloadClick = () => {
    dispatchPrintEventList({ filters: localFilterValues });
  };
  //------------------------------------------------
  const eventHighlight = onMapSelectedSiteId => {
    if (onMapSelectedSiteId != null) {
      const node = eventGridApi && eventGridApi.getRowNode(onMapSelectedSiteId);
      // Go to the highlighted node
      eventGridApi && eventGridApi.ensureNodeVisible(node, { position: 'top' });
      if (node) {
        if (!node.isSelected()) {
          node.setSelected(true);
        }
      }
    }
  };
  eventHighlight(onMapSelectedSiteId);
  return (
    <LayoutWithMap
      header="Events"
      editable={editable}
      cols={columns}
      data={events}
      // for creation / edit
      onEdit={onEdit}
      totalEntries={totalEntries}
      fetchData={fetchEvents}
      toggleModal={toggleModal}
      // render filters
      filtersRequired={filtersRequired}
      filters={filters({
        bizClients,
        category: localFilterValues.category,
        minDate: localFilterValues.dateFrom,
        services,
        incidentStatus: EVENT_INCIDENT_STATUS,
      })}
      linkedFilterFields={{ category: ['type'] }} // when category is changed, its dependent fields will be cleared
      forceRefresh={forceRefreshEvents}
      DetailComponent={DetailComponent}
      FormComponent={FormComponent}
      downloadable={downloadable}
      onDownloadClick={onDownloadClick}
      filterValues={localFilterValues}
      defaultFilterValues={defaultFilterValues}
      setFilters={(values, hasApplyFilter) => {
        updateFilter({
          values,
          hasApplyFilter,
          applyFilter: filters => {
            dispatchSetFilterKeyAction(true);
            dispatchSetFilterAction(filters);
          },
        });
      }}
      onUpdate={onUpdate}
      setGridAPI={gridAPIHandler}
      persistentStateName={PAGE_URLS.EVENTS.name}
      forceOpenDetailPanel={detailOpen[PAGE_URLS.EVENTS.link]}
      selectedID={onMapSelectedSiteId} // this will enable opening of detail panel on the actual list view from dashboard with or without highlighting the list view and map marker
      loading={loading}
    />
  );
};

const mapStateToProps = () => ({
  forceRefresh: false,
  closeDetailPanel: false,
});

Events.propTypes = {
  columns: PropTypes.array,
  filtersRequired: PropTypes.bool,
  filters: PropTypes.func,
  defaultFilterValues: PropTypes.object,
  downloadable: PropTypes.bool,
  editable: PropTypes.bool,
  DetailComponent: PropTypes.elementType,
  FormComponent: PropTypes.elementType,
  toggleModal: PropTypes.func,
  onEdit: PropTypes.bool,
  loading: PropTypes.bool,
};

export default connect(mapStateToProps, null)(Events);
